/**
 * Supplier Management Feature Types
 */

// ============================================================================
// CURRENCY
// ============================================================================

export type Currency = 'CAD' | 'USD';

export const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
];

export const DEFAULT_CURRENCY: Currency = 'CAD';

// ============================================================================
// SUPPLIER TYPES
// ============================================================================

/**
 * Base supplier record from the suppliers table
 */
export interface Supplier {
  id: string;
  businessId: string;
  name: string;
  contactEmail: string | null;
  phone: string | null;
  defaultCurrency: Currency | null;
  website: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Supplier from v_suppliers view with location aggregations
 */
export interface SupplierListItem extends Supplier {
  locationCount: number;
  primaryLocationCity: string | null;
  displayLocationText: string | null;
  businessName: string | null;
}

/**
 * Input for creating a new supplier
 */
export interface CreateSupplierInput {
  business_id: string;
  name: string;
  contact_email?: string | null;
  phone?: string | null;
  default_currency?: Currency | null;
  website?: string | null;
  notes?: string | null;
}

/**
 * Input for updating an existing supplier
 */
export interface UpdateSupplierInput {
  name?: string;
  contact_email?: string | null;
  phone?: string | null;
  default_currency?: Currency | null;
  website?: string | null;
  notes?: string | null;
}

// ============================================================================
// SUPPLIER LOCATION TYPES
// ============================================================================

/**
 * Base supplier location record from the supplier_locations table
 */
export interface SupplierLocation {
  id: string;
  businessId: string;
  supplierId: string;
  locationName: string | null;
  streetAddress: string | null;
  unit: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  notes: string | null;
  addressCacheId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Supplier location from v_supplier_locations view with computed fields
 */
export interface SupplierLocationView extends SupplierLocation {
  fullAddress: string | null;
  contactFullName: string | null;
  supplierName: string | null;
  // Geocode fields from address_cache
  lat: number | null;
  lng: number | null;
  geocodeStatus: string | null;
  geocodeAccuracy: string | null;
  geocodeFormatted: string | null;
  geocodeAttempts: number | null;
  geocodeError: string | null;
  geocodedAt: string | null;
  hasCoordinates: boolean;
  // Business context
  businessName: string | null;
}

/**
 * Input for creating a new supplier location
 */
export interface CreateSupplierLocationInput {
  business_id: string;
  supplier_id: string;
  location_name?: string | null;
  street_address?: string | null;
  unit?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  notes?: string | null;
}

/**
 * Input for updating an existing supplier location
 */
export interface UpdateSupplierLocationInput {
  location_name?: string | null;
  street_address?: string | null;
  unit?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  notes?: string | null;
}

// ============================================================================
// MAPPING HELPERS
// ============================================================================

/**
 * Map database row to Supplier type (from suppliers table)
 */
export function mapSupplierFromDb(row: Record<string, unknown>): Supplier {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    name: row.name as string,
    contactEmail: row.contact_email as string | null,
    phone: row.phone as string | null,
    defaultCurrency: row.default_currency as Currency | null,
    website: row.website as string | null,
    notes: row.notes as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Map database row to SupplierListItem type (from v_suppliers view)
 */
export function mapSupplierListItemFromDb(row: Record<string, unknown>): SupplierListItem {
  return {
    ...mapSupplierFromDb(row),
    locationCount: row.location_count as number,
    primaryLocationCity: row.primary_location_city as string | null,
    displayLocationText: row.display_location_text as string | null,
    businessName: row.business_name as string | null,
  };
}

/**
 * Map database row to SupplierLocation type (from supplier_locations table)
 */
export function mapSupplierLocationFromDb(row: Record<string, unknown>): SupplierLocation {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    supplierId: row.supplier_id as string,
    locationName: row.location_name as string | null,
    streetAddress: row.street_address as string | null,
    unit: row.unit as string | null,
    city: row.city as string | null,
    province: row.province as string | null,
    postalCode: row.postal_code as string | null,
    country: row.country as string | null,
    phone: row.phone as string | null,
    email: row.email as string | null,
    contactFirstName: row.contact_first_name as string | null,
    contactLastName: row.contact_last_name as string | null,
    notes: row.notes as string | null,
    addressCacheId: row.address_cache_id as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Map database row to SupplierLocationView type (from v_supplier_locations view)
 */
export function mapSupplierLocationViewFromDb(row: Record<string, unknown>): SupplierLocationView {
  return {
    ...mapSupplierLocationFromDb(row),
    fullAddress: row.full_address as string | null,
    contactFullName: row.contact_full_name as string | null,
    supplierName: row.supplier_name as string | null,
    lat: row.lat as number | null,
    lng: row.lng as number | null,
    geocodeStatus: row.geocode_status as string | null,
    geocodeAccuracy: row.geocode_accuracy as string | null,
    geocodeFormatted: row.geocode_formatted as string | null,
    geocodeAttempts: row.geocode_attempts as number | null,
    geocodeError: row.geocode_error as string | null,
    geocodedAt: row.geocoded_at as string | null,
    hasCoordinates: row.has_coordinates as boolean,
    businessName: row.business_name as string | null,
  };
}
